import { S } from './state.js';
import { BAL } from './config.js';
import { TIERS } from './data/items.js';
import { WEAPONS, WEAPON_IDX } from './data/weapons.js';
import { rnd } from './fx.js';
import { currentBiome, worldFromScreenX, worldFromScreenY } from './main.js';
import { SHRINE_STYLE, bloodCost, shrineItemPrice, vaultPrice } from './economy.js';
import { cam } from './meter.js';
import { player } from './entities.js';
import { worldLimA } from './world.js';
import { ctx } from './canvas.js';
import { aim } from './input.js';
import { gambleCost } from './shrines.js';
import { roundRect } from './screens.js';
import { shieldConc } from './combat.js';
// ---- Rendering ----
export function bg() {
  const bm = currentBiome();                 // palette shifts every 5 waves
  const g = ctx.createLinearGradient(0, 0, 0, S.H);
  g.addColorStop(0, bm.sky[0]);
  g.addColorStop(0.55, bm.sky[1]);
  g.addColorStop(1, bm.sky[2]);
  ctx.fillStyle = g; ctx.fillRect(0, 0, S.W, S.H);

  // parallax starfield (scrolls in both axes as you move — an arena feel)
  ctx.fillStyle = bm.star;
  for (let i = 0; i < 90; i++) {
    const sx = ((i * 137.5 - cam.x * 0.15) % (S.W + 40) + S.W + 40) % (S.W + 40) - 20;
    const sy = ((i * 89.3 - cam.y * 0.15) % (S.H + 40) + S.H + 40) % (S.H + 40) - 20;
    const tw = 0.5 + 0.5 * Math.sin(S.frame * 0.04 + i);
    ctx.globalAlpha = 0.2 + tw * 0.5;
    ctx.fillRect(sx, sy, 2, 2);
  }
  ctx.globalAlpha = 1;
}
// ---- glow sprites (0.19 memory fix): radial gradients were allocated PER ENTITY PER FRAME in the
// coin / enemy / pickup / shrine draws — at the 200-enemy cap over a coin-rich field that's ~40,000
// short-lived gradient objects per second of pure GC churn. The browser does reclaim them, but long
// sessions kept multi-GB heaps between collections. Each distinct glow renders ONCE to a small
// offscreen canvas and gets stamped with drawImage (also simply faster). Keys are the two gradient
// stop colors — a few dozen distinct glows exist in total, so the cache is bounded.
export const _glowCache = Object.create(null);
export function glowSprite(inner, outer) {
  const k = inner + '|' + outer;
  let c = _glowCache[k];
  if (!c) {
    c = document.createElement('canvas'); c.width = c.height = 64;
    const g = c.getContext('2d');
    const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    gr.addColorStop(0, inner); gr.addColorStop(1, outer);
    g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
    _glowCache[k] = c;
  }
  return c;
}
export function drawCoins() {
  for (const c of S.coins) {
    const x = c.x - cam.x, y = c.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    const sz = c.worth > 1 ? Math.min(1.7, 1 + Math.log2(c.worth) * 0.14) : 1;   // consolidated coins read as fatter denominations
    const w = (5 + 2 * Math.sin(S.frame * 0.12 + c.ph)) * sz;   // pseudo-spin
    const gr = 16 * sz;
    ctx.drawImage(glowSprite('rgba(255,214,110,.55)', 'rgba(255,214,110,0)'), x - gr, y - gr, gr * 2, gr * 2);
    ctx.fillStyle = '#ffd66e';
    ctx.beginPath(); ctx.ellipse(x, y, Math.abs(w), 8 * sz, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#c79a2e';
    ctx.beginPath(); ctx.ellipse(x, y, Math.abs(w) * 0.5, 5 * sz, 0, 0, Math.PI * 2); ctx.fill();
  }
}

export function drawPickups() {
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (const p of S.pickups) {
    if (p.got) continue;
    ctx.globalAlpha = 1;
    const x = p.x - cam.x, y = p.y - cam.y + Math.sin(S.frame * 0.1 + p.ph) * 3;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    // weapon drops fade & blink in their last ~2s before despawning
    if (p.kind === 'weapon' && p.life != null && p.life < 120) { if (S.frame % 8 < 4) continue; ctx.globalAlpha = p.life / 120; }
    const col = p.kind === 'heart' ? '#ff6b8a' : p.kind === 'weapon' ? '#ffd66e' : '#7fe6ff';
    const big = p.kind === 'weapon';
    if (big) {   // weapons get a pulsing ring so they read as a special drop
      ctx.strokeStyle = hexA(col, 0.8); ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, 22 + Math.sin(S.frame * 0.1) * 3, 0, Math.PI * 2); ctx.stroke();
    }
    const gr2 = big ? 26 : 18;
    ctx.drawImage(glowSprite(hexA(col, 0.5), hexA(col, 0)), x - gr2, y - gr2, gr2 * 2, gr2 * 2);
    const ic = p.kind === 'heart' ? '❤️' : p.kind === 'weapon' ? (WEAPONS[WEAPON_IDX[p.wkey]] || WEAPONS[0]).icon : '🔩';
    ctx.font = (big ? 22 : 18) + 'px system-ui'; ctx.fillText(ic, x, y + 1);
    if (p.kind === 'weapon') {   // name always; "[E] take" prompt only when you're close enough to grab it
      const near = Math.hypot(p.x - player.x, p.y - player.y) < 64;
      const wn = (WEAPONS[WEAPON_IDX[p.wkey]] || WEAPONS[0]).name;
      ctx.font = 'bold 11px system-ui'; ctx.fillStyle = near ? '#ffd66e' : '#9fb0e0';
      ctx.fillText(near ? '[E] swap to ' + wn : wn, x, y + 34);
    }
  }
  ctx.globalAlpha = 1; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}

export function drawEffects() {
  for (const e of S.effects) {
    if (e.type === 'spark') {
      ctx.globalAlpha = Math.max(0, e.life);
      ctx.strokeStyle = '#cfe6ff'; ctx.lineWidth = 2.5;
      ctx.shadowColor = '#8fd0ff'; ctx.shadowBlur = 12 * S.fxShadow;
      ctx.beginPath();
      for (let i = 0; i < e.pts.length; i++) {
        const X = e.pts[i].x - cam.x, Y = e.pts[i].y - cam.y;
        if (i === 0) { ctx.moveTo(X, Y); continue; }
        const pp = e.pts[i - 1];
        const mx = (pp.x + e.pts[i].x) / 2 - cam.x + (rnd() - 0.5) * 18;
        const my = (pp.y + e.pts[i].y) / 2 - cam.y + (rnd() - 0.5) * 18;
        ctx.lineTo(mx, my); ctx.lineTo(X, Y);
      }
      ctx.stroke(); ctx.shadowBlur = 0;
    } else if (e.type === 'shock') {
      ctx.globalAlpha = Math.max(0, e.life) * 0.85;
      ctx.strokeStyle = e.hostile ? '#ff5a6e' : '#7dffb0'; ctx.lineWidth = 3;   // hostile = bomber blast
      ctx.beginPath(); ctx.arc(e.x - cam.x, e.y - cam.y, e.r, 0, Math.PI * 2); ctx.stroke();
    } else if (e.type === 'boostflash') {
      ctx.globalAlpha = Math.max(0, e.life);
      ctx.strokeStyle = '#ff9d6e'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(e.x - cam.x, e.y - cam.y, (1 - e.life) * 60 + 10, 0, Math.PI * 2); ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
}

export function hexA(hex, a) {   // '#rrggbb' -> 'rgba(r,g,b,a)'
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

export function drawShrines() {
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (const s of S.shrines) {
    if (s.taken) continue;
    const x = s.x - cam.x, y = s.y - cam.y + Math.sin(S.frame * 0.04 + s.ph) * 6;
    if (x < -S.W * 0.7 || x > S.W * 2.1) continue;
    const st = SHRINE_STYLE[s.kind];                       // set for blood/reroll/pandora; undefined for item caches
    const col = st ? st.col : TIERS[s.item.tier].color;    // item cache: tinted by the BEST rarity inside
    const icon = (s.kind === 'reroll' || s.kind === 'pandora' || s.kind === 'vault') ? st.icon : s.kind === 'item' ? '🛒' : s.item.icon;   // blood shows the item you'd bleed for; caches show the cart
    const near = Math.hypot(s.x - player.x, s.y - player.y) < player.r + 72;
    let cost = 0, afford = true, special, topLabel, bottom;
    if (s.kind === 'blood')        { cost = bloodCost();   afford = player.hp > cost + 1; special = true; topLabel = st.tag; bottom = (near && afford ? '[E] ❤ ' : '❤ ') + cost; }
    else if (s.kind === 'reroll')  { cost = gambleCost();  afford = S.coinsRun >= cost;     special = true; topLabel = st.tag; bottom = (near ? '[E] ' : '') + '🪙 ' + cost; }
    else if (s.kind === 'pandora') { special = true; topLabel = st.tag; bottom = near ? '[E] OPEN' : 'OPEN'; }
    else if (s.kind === 'vault')   { cost = vaultPrice(); afford = S.coinsRun >= cost; special = true; topLabel = st.tag; bottom = (near && afford ? '[E] ' : '') + '🪙 ' + cost; }
    else {   // item cache: 3 priced items inside — walk in to browse; best rarity's color + cheapest entry price
      cost = Math.min(...s.choices.map(shrineItemPrice));
      afford = S.coinsRun >= cost;
      special = s.item.tier === 'legendary' || s.item.tier === 'lunar' || s.item.tier === 'mythic';
      topLabel = 'CACHE ×3';
      bottom = 'from 🪙 ' + cost;
    }

    // pulsing ring on rare / blood shrines — the "god run" cue
    if (special) {
      const pr = 32 + Math.sin(S.frame * 0.08 + s.ph) * 4;
      ctx.strokeStyle = hexA(col, 0.7); ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, pr, 0, Math.PI * 2); ctx.stroke();
    }

    // glow
    const R = special ? 56 : 44;
    ctx.drawImage(glowSprite(hexA(col, afford ? 0.55 : 0.3), hexA(col, 0)), x - R, y - R, R * 2, R * 2);

    // pod
    ctx.fillStyle = s.kind === 'blood' ? 'rgba(40,12,18,.9)' : afford ? 'rgba(16,22,40,.88)' : 'rgba(28,32,50,.8)';
    ctx.strokeStyle = afford ? col : hexA(col, 0.5);
    ctx.lineWidth = special ? 3 : 2;
    roundRect(x - 23, y - 23, 46, 46, 11); ctx.fill(); ctx.stroke();

    // icon, banner, cost
    ctx.font = '24px system-ui'; ctx.fillStyle = '#fff';
    ctx.fillText(icon, x, y + 1);
    ctx.font = 'bold 10px system-ui'; ctx.fillStyle = col;
    ctx.fillText(topLabel, x, y - 34);
    ctx.font = 'bold 13px system-ui';
    ctx.fillStyle = afford ? (s.kind === 'blood' ? '#ff7a8a' : s.kind === 'pandora' ? '#ffe08a' : '#ffd66e') : '#9aa6c8';
    ctx.fillText(bottom, x, y + 40);
  }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}

export function drawPlayer() {
  const x = player.x - cam.x, y = player.y - cam.y;

  // trail
  for (let i = 0; i < player.trail.length; i++) {
    const tp = player.trail[i];
    ctx.globalAlpha = (i / player.trail.length) * 0.5;
    ctx.fillStyle = '#7fd0ff';
    const r = player.r * (i / player.trail.length);
    ctx.beginPath(); ctx.arc(tp.x - cam.x, tp.y - cam.y, r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;

  // aim direction toward the cursor (a little barrel)
  const ang = Math.atan2(worldFromScreenY(aim.y) - player.y, worldFromScreenX(aim.x) - player.x);
  ctx.strokeStyle = 'rgba(189,243,255,.8)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(x, y);
  ctx.lineTo(x + Math.cos(ang) * (player.r + 12), y + Math.sin(ang) * (player.r + 12)); ctx.stroke();
  ctx.lineCap = 'butt';

  // i-frame feedback after taking a hit — strobe normally, steady dim if flashing is reduced
  const blink = player.iFrames > 0 && (S.save.settings.reduceFlash || S.frame % 8 < 4);

  // body glow (sprite-stamped — see glowSprite)
  const pgR = player.r * 3;
  ctx.drawImage(glowSprite('#bfe6ffaa', '#bfe6ff00'), x - pgR, y - pgR, pgR * 2, pgR * 2);

  ctx.globalAlpha = blink ? 0.4 : 1;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.arc(x, y, player.r, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#4aa3ff';
  ctx.beginPath(); ctx.arc(x, y, player.r * 0.55, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;

  // shield bubble — a protective sphere you can SEE, so blocked hits read instantly (one ring per charge)
  if (player.shield > 0) {
    const br = player.r + 8;
    ctx.globalAlpha = 0.10 + 0.05 * Math.sin(S.frame * 0.14);
    ctx.fillStyle = '#7fb6ff';
    ctx.beginPath(); ctx.arc(x, y, br + (player.shield - 1) * 3, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 0.55 + 0.25 * Math.sin(S.frame * 0.14);
    ctx.strokeStyle = '#bcd8ff'; ctx.lineWidth = 1.6;
    for (let i = 0; i < player.shield; i++) {
      ctx.beginPath(); ctx.arc(x, y, br + i * 3, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // invulnerability ring — tells you EXACTLY when you can't be hit: cyan during a dash, white after a hit
  if (player.iFrames > 0) {
    const dashing = player.dashing > 0;
    ctx.globalAlpha = 0.45 + 0.4 * Math.abs(Math.sin(S.frame * 0.45));
    ctx.strokeStyle = dashing ? 'rgba(143,252,255,.95)' : 'rgba(255,255,255,.8)';
    ctx.lineWidth = dashing ? 3 : 2;
    ctx.setLineDash(dashing ? [] : [5, 5]);
    ctx.beginPath(); ctx.arc(x, y, player.r + 4, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  // stage-2 indicators: Siege brace (gold ring when planted) + Charge Lance build-up arc
  if (S.EVO2 && S.EVO2.siege && S.stillT >= 60) {
    ctx.strokeStyle = 'rgba(255,214,110,.6)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, player.r + 14, 0, Math.PI * 2); ctx.stroke();
  }
  if (S.EVO2 && S.EVO2.charge && S.chargeT > 0) {
    ctx.strokeStyle = S.chargeT >= 90 ? '#ffd66e' : 'rgba(143,252,255,.85)'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(x, y, player.r + 10, -Math.PI / 2, -Math.PI / 2 + (S.chargeT / 90) * Math.PI * 2); ctx.stroke();
  }
  // affix status rings — Venomous poison (green) / Chilling slow (ice-blue)
  if (player.poisonT > 0) {
    ctx.strokeStyle = 'rgba(139,224,78,.8)'; ctx.lineWidth = 2;
    ctx.globalAlpha = 0.5 + 0.4 * Math.sin(S.frame * 0.3);
    ctx.beginPath(); ctx.arc(x, y, player.r + 5, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1;
  }
  if (player.slowT > 0) {
    ctx.strokeStyle = 'rgba(143,216,255,.8)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, player.r + 8, 0, Math.PI * 2); ctx.stroke();
  }
}

export function drawParticles() {
  for (const p of S.particles) {
    ctx.globalAlpha = Math.max(0, p.life);
    ctx.fillStyle = p.color;
    ctx.beginPath(); ctx.arc(p.x - cam.x, p.y - cam.y, p.r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.textAlign = 'center';
  for (const t of S.texts) {
    ctx.globalAlpha = t.life; ctx.fillStyle = '#ffd66e';
    ctx.font = 'bold 20px system-ui';
    ctx.fillText(t.val, t.x - cam.x, t.y - cam.y);   // centered so longer effect text (e.g. pickups) reads cleanly
  }
  ctx.globalAlpha = 1; ctx.textAlign = 'left';
  // floating damage numbers
  ctx.textAlign = 'center';
  for (const dt of S.dmgTexts) {
    ctx.globalAlpha = Math.max(0, dt.life);
    ctx.fillStyle = dt.crit ? '#ffe08a' : '#eaf2ff';
    ctx.font = (dt.crit ? 'bold 17px' : '13px') + ' system-ui';
    ctx.fillText(dt.val, dt.x - cam.x, dt.y - cam.y);
  }
  ctx.globalAlpha = 1; ctx.textAlign = 'left';
}

export function fmtTime(frames) {
  const s = Math.floor(frames / 60);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
// short magnitude label: 950, 12.3k, 4.7M, 8.5B, 848T … then exponential past the named units (deep
// runs push damage well past 1e15 — the old "always k" gave unreadable strings like 847989599227.7k)
export function abbr(n) {
  n = Math.round(n); const sign = n < 0 ? '-' : ''; n = Math.abs(n);
  if (n < 1000) return sign + n;
  const units = ['k', 'M', 'B', 'T', 'Qa', 'Qi'];
  let i = -1;
  while (n >= 1000 && i < units.length - 1) { n /= 1000; i++; }
  return sign + (n >= 1000 ? n.toExponential(1) : n.toFixed(1) + units[i]);
}
// theoretical GUN dps — paper math for the HUD/pause readouts, now incl. crit expectation and the
// coin-powered mults (Gold Fever / Dividend) it silently ignored. It still can't see procs/DoTs/blasts —
// the damage METER measures those — so it's labeled "gun ~X dps" vs the meter's actual "/s" (TODO fix, 0.16)
export function gunCadence(wp) { return S.FIRE_COOLDOWN * S.fvFire * wp.cdMul * ((S.EVO && S.EVO.cdX) || 1); }   // base frames/shot — the fire site multiplies Adrenaline & spin-up on top
// universal fire-rate cap (playtest: "fire rate cap kéne nagyon" → "cap it on everything"): every gun
// floors at 10 shots/s, arc lower still (a chain per trigger hits half the screen). Fire rate past the
// floor isn't wasted — it overflows into damage (×2 cap, arc excluded), so the cap bends, not breaks.
export function gunFloor(w) { return w.mode === 'arc' ? BAL.floors.arc : BAL.floors.gun; }
export function paperDps() {
  const wp = WEAPONS[S.weapon], effShots = (wp.shots || 1) + ((S.EVO && S.EVO.shots) || 0) + (S.MULTISHOT - 1) + S.PELLETS;
  const rawCd = gunCadence(wp), fl = gunFloor(wp);
  return (S.DAMAGE + S.divBonus) * S.fvDmg * S.goldMult * S.spendMult * wp.dmgMul * ((S.EVO && S.EVO.dmgX) || 1)
       * (1 + S.CRIT * (S.CRITMULT - 1)) * effShots
       * (wp.mode === 'arc' ? 1 : Math.min(BAL.overflow.rofCap, S.ROF_OVERFLOW * (rawCd < fl ? fl / Math.max(0.5, rawCd) : 1)))
       * (60 / Math.max(fl, rawCd));
}
// edge-of-screen arrows to off-screen shrines & weapon drops (player request — "so they don't get
// lost"). Hearts/shards are deliberately excluded; only the things you'd actually walk back for.
// Suppressed inside the boss arena: you can't leave the ring anyway.
export function drawPickupArrows() {
  if (S.arena.active) return;
  const targets = [];
  for (const s of S.shrines) {
    if (s.taken) continue;
    targets.push({ x: s.x, y: s.y, icon: s.kind === 'item' ? '🛒' : SHRINE_STYLE[s.kind].icon,
                   col: SHRINE_STYLE[s.kind] ? SHRINE_STYLE[s.kind].col : TIERS[s.item.tier].color });
  }
  for (const p of S.pickups) {
    if (p.got || p.kind !== 'weapon') continue;
    targets.push({ x: p.x, y: p.y, icon: (WEAPONS[WEAPON_IDX[p.wkey]] || WEAPONS[0]).icon, col: '#ffd66e', life: p.life });
  }
  if (!targets.length) return;
  targets.sort((a, b) => Math.hypot(a.x - player.x, a.y - player.y) - Math.hypot(b.x - player.x, b.y - player.y));
  const psx = player.x - cam.x, psy = player.y - cam.y, m = 30;   // world draws zoomed around the player's screen pos
  let shown = 0;
  ctx.save();
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (const t of targets) {
    if (shown >= 7) break;                                        // glanceable, not a fireworks show
    const d = Math.hypot(t.x - player.x, t.y - player.y);         // no horizon: shrines persist anywhere on the map now
    const sx = psx + (t.x - player.x) * S.zoom, sy = psy + (t.y - player.y) * S.zoom;
    if (sx > -10 && sx < S.W + 10 && sy > -10 && sy < S.H + 10) continue;   // visible — no arrow needed
    if (t.life != null && t.life < 120 && S.frame % 8 < 4) continue;      // expiring weapon drop: blink like the drop itself
    const ex = Math.max(m, Math.min(S.W - m, sx)), ey = Math.max(m, Math.min(S.H - m, sy));
    const ang = Math.atan2(sy - ey, sx - ex);                     // residual after clamping points AT the target
    ctx.globalAlpha = Math.max(0.8, Math.min(0.95, 1.15 - d / 8000));   // bold at any distance (was fading to barely-there far away)
    const ix = ex - Math.cos(ang) * 19, iy = ey - Math.sin(ang) * 19;
    ctx.fillStyle = 'rgba(8,12,26,.78)';                          // dark backing so emoji stay readable on bright scenes
    ctx.beginPath(); ctx.arc(ix, iy, 14, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = t.col; ctx.lineWidth = 2;                   // colored rim ties the badge to its chevron
    ctx.stroke();
    ctx.fillStyle = t.col;                                        // chevron tip on the very edge, aimed outward
    ctx.beginPath();
    ctx.moveTo(ex + Math.cos(ang) * 14, ey + Math.sin(ang) * 14);
    ctx.lineTo(ex + Math.cos(ang + 2.4) * 9, ey + Math.sin(ang + 2.4) * 9);
    ctx.lineTo(ex + Math.cos(ang - 2.4) * 9, ey + Math.sin(ang - 2.4) * 9);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(8,12,26,.8)'; ctx.lineWidth = 1.5; ctx.stroke();   // dark outline pops on any background
    ctx.font = '17px system-ui';
    ctx.fillText(t.icon, ix, iy + 1);
    shown++;
  }
  ctx.restore();
}
export function drawMinimap() {
  // touch: smaller, and shifted left so the DASH / E buttons own the corner
  const R = S.isTouch ? 64 : 76, cx = S.W - R - (S.isTouch ? 132 : 18), cy = S.H - R - 18, range = 1500, sc = R / range;
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(8,12,26,0.55)'; ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 1; ctx.stroke();
  ctx.clip();   // keep all blips inside the radar disc
  // the void's edge — so the wall never surprises you (BLACKOUT included); sampled, it's a blob now
  ctx.strokeStyle = 'rgba(160,120,255,0.45)'; ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 48; i++) {
    const a = i / 48 * Math.PI * 2, r = worldLimA(a);
    const bx = cx + (Math.cos(a) * r - player.x) * sc, by = cy + (Math.sin(a) * r - player.y) * sc;
    i ? ctx.lineTo(bx, by) : ctx.moveTo(bx, by);
  }
  ctx.closePath(); ctx.stroke();
  ctx.fillStyle = 'rgba(150,170,220,0.30)';   // terrain
  for (const k of S.obstacles) {
    const bx = (k.x - player.x) * sc, by = (k.y - player.y) * sc;
    if (bx * bx + by * by > R * R) continue;
    ctx.beginPath(); ctx.arc(cx + bx, cy + by, Math.max(1.5, k.r * sc), 0, Math.PI * 2); ctx.fill();
  }
  if (S.arena.active) {
    ctx.strokeStyle = 'rgba(255,90,90,0.5)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(cx + (S.arena.x - player.x) * sc, cy + (S.arena.y - player.y) * sc, S.arena.r * sc, 0, Math.PI * 2); ctx.stroke();
  }
  for (const s of S.shrines) {
    if (s.taken) continue;
    const bx = (s.x - player.x) * sc, by = (s.y - player.y) * sc;
    if (bx * bx + by * by > R * R) continue;
    ctx.fillStyle = SHRINE_STYLE[s.kind] ? SHRINE_STYLE[s.kind].col : TIERS[s.item.tier].color;
    ctx.fillRect(cx + bx - 1.5, cy + by - 1.5, 3, 3);
  }
  for (const p of S.pickups) {
    if (p.got || p.kind !== 'weapon') continue;
    const bx = (p.x - player.x) * sc, by = (p.y - player.y) * sc;
    if (bx * bx + by * by > R * R) continue;
    ctx.fillStyle = '#ffd66e'; ctx.fillRect(cx + bx - 1.5, cy + by - 1.5, 3, 3);
  }
  for (const e of S.enemies) {
    if (e.dead) continue;
    const bx = (e.x - player.x) * sc, by = (e.y - player.y) * sc;
    if (bx * bx + by * by > R * R) continue;
    if (e.kind === 'boss') { ctx.fillStyle = '#ff8a3a'; ctx.beginPath(); ctx.arc(cx + bx, cy + by, 3.5, 0, Math.PI * 2); ctx.fill(); }
    else { ctx.fillStyle = (e.elite || e.affix) ? '#ffd66e' : '#ff6b7e'; ctx.fillRect(cx + bx - 1, cy + by - 1, 2.5, 2.5); }
  }
  ctx.restore();
  ctx.fillStyle = '#7fd0ff'; ctx.beginPath(); ctx.arc(cx, cy, 2.5, 0, Math.PI * 2); ctx.fill();   // you, at center
  // off-radar boss bearing
  for (const e of S.enemies) {
    if (e.kind !== 'boss' || e.dead) continue;
    const bx = (e.x - player.x) * sc, by = (e.y - player.y) * sc, bd = Math.hypot(bx, by);
    if (bd > R) { const a = Math.atan2(by, bx); ctx.fillStyle = '#ff8a3a'; ctx.beginPath(); ctx.arc(cx + Math.cos(a) * (R - 4), cy + Math.sin(a) * (R - 4), 3, 0, Math.PI * 2); ctx.fill(); }
  }
}
// live damage meter — compact panel, bottom-left, top sources this run (full breakdown in journal → DAMAGE)
// how tall the damage meter will draw this frame — the stats panel (B) stacks directly above it
export function dmgMeterHeight() {
  if (!S.save.settings.dmgMeter) return 0;
  let n = 0;
  for (const k in S.dmgBySrc) if (S.dmgBySrc[k] > 0) n++;
  return n ? 17 + Math.min(5, n) * 15 + 6 : 0;
}
// bottom anchor for the bottom-left stack (meter + stats panel). 0.19 UI pass: a panel whose right
// edge reaches the centered XP bar DUCKS above it instead of overlaying it (the 0.18 stats panel sat
// on the bar on most screens); on touch the whole stack also clears the joystick zone.
export function hudBottomY(panelW) {
  const xpLeft = (S.W - Math.min(380, S.W * 0.36)) / 2 - 8;
  return S.H - (24 + panelW > xpLeft ? 56 : 22) - (S.isTouch ? 154 : 0);
}
// STATS PANEL (0.18, toggle: B) — the live derived numbers builds are made of. Same glass styling as
// the damage meter; sits above it so the left edge reads time → coins → HP → stats → meter.
export function drawStatsPanel() {
  if (!S.save.settings.statsPanel) return;
  const wpn = WEAPONS[S.weapon];
  const narrow = S.W < 700;
  const effDmg = (S.DAMAGE + S.divBonus + S.ggBonus) * S.fvDmg * S.goldMult * S.spendMult * S.ciMult * S.pyroMult;
  const effShots = (wpn.shots || 1) + ((S.EVO && S.EVO.shots) || 0) + (S.MULTISHOT - 1) + S.PELLETS;
  const armorCapNow = S.arch.tank >= 3 ? BAL.player.armorCapTank : BAL.player.armorCap;
  const atFloor = gunCadence(wpn) <= gunFloor(wpn);
  // cap flags (0.19, player request: "every value at its own cap could be red"): 2 = at a hard cap
  // (red — more copies are wasted unless an OVERFLOW channel eats them), 1 = past a soft knee (amber)
  const L = [   // [label, value, capFlag]
    ['dmg',    abbr(Math.round(effDmg)) + (effDmg > S.DAMAGE + 0.5 ? '  (' + abbr(Math.round(S.DAMAGE)) + ')' : ''), 0],
    ['rate',   (60 / Math.max(gunFloor(wpn), gunCadence(wpn))).toFixed(1) + '/s' + (atFloor ? ' FLOOR→dmg' : S.RATE_KNEED ? ' knee' : ''), atFloor ? 2 : S.RATE_KNEED ? 1 : 0],
    ['shots',  effShots + (S.PELLETS ? ' (+' + S.PELLETS + ' pellets)' : ''), 0],
    ['pierce', S.PIERCE + (wpn.pierceBonus || 0), 0],
    ['crit',   Math.round(Math.min(0.95, S.CRIT) * 100) + '% ×' + S.CRITMULT.toFixed(1), S.CRIT >= 0.95 ? 2 : 0],
    ['range',  Math.round(S.LASER_RANGE), 0],
    ['move',   Math.min(13, S.MAX_SPEED * 0.32 * S.fvSpeed).toFixed(1), S.MAX_SPEED * 0.32 * S.fvSpeed >= 13 ? 2 : 0],
    ['HP',     Math.ceil(Math.max(0, player.hp)) + '/' + S.MAX_HP + (S.REGEN ? ' +' + (+S.REGEN.toFixed(1)) + '/s' : ''), 0],
    ['armor',  Math.round(S.ARMOR * 100) + '% (cap ' + Math.round(armorCapNow * 100) + '%)', S.ARMOR >= armorCapNow - 0.001 ? 2 : 0],
    ['dodge',  Math.round(S.DODGE * 100) + '%', S.DODGE >= BAL.player.dodgeCap ? 2 : 0],
    ['shield', player.shield + '/' + S.MAX_SHIELD + (S.MAX_SHIELD ? ' · ' + (S.SHIELD_RECHARGE / 60).toFixed(1) + 's ×' + shieldConc() : ''), 0],
    ['burn',   Math.round(S.BURN_CH * 100) + '% · ' + abbr(Math.round(S.BURN_DPS)) + '/s', S.BURN_CH_RAW > S.BURN_CH ? 2 : 0],
    ['poison', Math.round(S.POISON_CH * 100) + '%' + (S.POISON_TICK_MUL > 1 ? ' ×' + S.POISON_TICK_MUL.toFixed(2) : ''), S.POISON_CH_RAW > S.POISON_CH ? 2 : 0],
    ['light.', '×' + S.LIGHTNING_MUL.toFixed(2), 0],
    ['shock',  '×' + S.SHOCK_MUL.toFixed(2), 0],
    ['proc',   '×' + S.procChanceMult.toFixed(2), 0],
    ['area',   '×' + S.RADIUS_MUL.toFixed(2), 0],
    ['leech',  (+((S.LIFESTEAL_HIT + (S.mods.lifesteal || 0)).toFixed(1))) + '/hit', 0],
  ];
  const cols = 2, perCol = Math.ceil(L.length / cols);
  const w = narrow ? 240 : 300, rowH = narrow ? 11 : 13, titleH = 16, padX = 8;
  const h = titleH + perCol * rowH + 6;
  const mh = dmgMeterHeight();
  let x = 24, y = (mh ? hudBottomY(190) - mh - 8 : hudBottomY(w)) - h;   // stacks above the meter; both duck under the XP bar (0.19)
  if (y < 224) { x = 24 + 190 + 12; y = hudBottomY(x + w - 24) - h; }    // short screen: beside the meter instead of climbing into the item bar
  ctx.save();
  ctx.fillStyle = S.isTouch ? 'rgba(8,12,26,.42)' : 'rgba(8,12,26,.6)'; roundRect(x, y, w, h, 8); ctx.fill();   // see-through on touch — it sits over the play field
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left'; ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 10px system-ui';
  ctx.fillText('📊 STATS', x + padX, y + titleH / 2 + 2);
  ctx.textAlign = 'right'; ctx.fillStyle = '#8a97c4'; ctx.font = '9px system-ui';
  ctx.fillText('red = capped · B hides', x + w - padX, y + titleH / 2 + 2);
  const colW = (w - padX * 2) / cols;
  ctx.font = (narrow ? 9 : 10) + 'px system-ui';
  L.forEach(([lab, val, cap], i) => {
    const cx = x + padX + Math.floor(i / perCol) * colW;
    const ry = y + titleH + (i % perCol) * rowH + rowH / 2;
    ctx.textAlign = 'left'; ctx.fillStyle = cap === 2 ? '#ff7a8a' : cap === 1 ? '#ffd24a' : '#7d89b8';
    ctx.fillText(lab, cx, ry);
    ctx.textAlign = 'right'; ctx.fillStyle = cap === 2 ? '#ff9aa6' : cap === 1 ? '#ffd24a' : '#e6ecff';
    ctx.fillText(String(val), cx + colW - 10, ry);
  });
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}
